import { useModal } from "@ebay/nice-modal-react";
import { useCallback, useMemo } from "react";
import { useShallow } from "zustand/react/shallow";

import DragDropList from "components/DragDropList";
import {
  Column,
  ColumnId,
  ColumnMode,
  RenderItemContentProps,
} from "components/DragDropList/types";
import DropEmptyStateDoubleColumn from "components/DropEmptyState/DoubleColumn";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import DrawerSimple from "ds/components/DrawerNew/Simple";
import { TableColumnMeta } from "ds/components/Table/types";
import Typography from "ds/components/Typography";
import { TrackAnalyticsEventProperties } from "shared/Analytics";
import DragDropListDefaultItem from "components/DragDropList/DefaultItem";

import { selectTableSettingsConfig } from "../store/useTableStore/selectors";
import { TableGenericStore } from "../store/useTableStore/types";
import { showResetConfirmation } from "./ResetConfirmation";

const getLabel = (columns: Record<string, TableColumnMeta>) => (key: string) =>
  columns[key].title || "";

const TABLE_CONFIG_CHANGED_ANALYTIC_EVENT = "Table configuration changed";

export type TableSettingsDrawerProps = {
  store: TableGenericStore;
  trackAnalytics?: (title: string, properties?: TrackAnalyticsEventProperties) => void;
};

const TableSettingsDrawerContent = ({ store, trackAnalytics }: TableSettingsDrawerProps) => {
  const drawer = useModal();

  const { tableSettingsConfig, columnsState, setColumnsOrder, resetColumnsConfig } = store(
    useShallow((state) => ({
      tableSettingsConfig: selectTableSettingsConfig(state),
      columnsState: state.columnsState,
      setColumnsOrder: state.setColumnsOrder,
      resetColumnsConfig: state.resetColumnsConfig,
    }))
  );

  const resetConfig = useCallback(() => {
    resetColumnsConfig();
    trackAnalytics?.(TABLE_CONFIG_CHANGED_ANALYTIC_EVENT, { reset: true });
    drawer.hide();
  }, [drawer, resetColumnsConfig, trackAnalytics]);

  const onChange = useCallback(
    ({ first, second }: { first: Column<string>; second: Column<string> }) => {
      const config = {
        visible: first.map(({ value }) => value),
        hidden: second.map(({ value }) => value),
      };
      trackAnalytics?.(TABLE_CONFIG_CHANGED_ANALYTIC_EVENT, { reset: false, ...config });
      setColumnsOrder(config);
    },
    [trackAnalytics, setColumnsOrder]
  );

  const firstColumn = useMemo(() => {
    return tableSettingsConfig.visibleColumns.map((column) => ({
      value: column,
      hidden: tableSettingsConfig.nonConfigurableColumns.includes(column),
    }));
  }, [tableSettingsConfig.visibleColumns, tableSettingsConfig.nonConfigurableColumns]);

  const secondColumn = useMemo(() => {
    return tableSettingsConfig.hiddenColumns.map((column) => ({
      value: column,
      hidden: tableSettingsConfig.nonConfigurableColumns.includes(column),
    }));
  }, [tableSettingsConfig.hiddenColumns, tableSettingsConfig.nonConfigurableColumns]);

  const renderItemContent = useCallback(
    ({
      id,
      dragging,
      handleRef,
      handleListeners,
      handleAttributes,
      disabled,
    }: RenderItemContentProps<string, undefined>) => {
      return (
        <DragDropListDefaultItem
          dragging={dragging}
          handleRef={handleRef}
          handleListeners={handleListeners}
          handleAttributes={handleAttributes}
          disabled={disabled}
          titleComponent={
            <Typography tag="span" variant="p-t7">
              {getLabel(columnsState)(id)}
            </Typography>
          }
        />
      );
    },
    [columnsState]
  );

  const renderEmptyColumnState = useCallback(
    (isDragActive: boolean, columnId: ColumnId) => (
      <DropEmptyStateDoubleColumn isDragActive={isDragActive} columnId={columnId} />
    ),
    []
  );

  return (
    <DrawerSimple>
      <DrawerHeader justify="between">
        <DrawerHeaderTitle title="Customize list" />
        <DrawerCloseIcon />
      </DrawerHeader>
      <DrawerBody noPadding>
        <Box direction="column" grow="1" fullWidth padding="x-large">
          <DragDropList
            renderItemContent={renderItemContent}
            onChange={onChange}
            columnMode={ColumnMode.Double}
            firstColumn={firstColumn}
            firstColumnTitle="Visible columns"
            secondColumnTitle="Hidden columns"
            secondColumn={secondColumn}
            renderEmptyColumnState={renderEmptyColumnState}
            vertical
            verticalAxisOnly
            hasDroppableCardWrapper
          />
        </Box>
      </DrawerBody>
      <DrawerFooter>
        <Button onPress={() => showResetConfirmation({ onReset: resetConfig })} variant="secondary">
          Reset to default
        </Button>
      </DrawerFooter>
    </DrawerSimple>
  );
};

TableSettingsDrawerContent.displayName = "DS.Table.SettingsDrawerContent";

export default TableSettingsDrawerContent;
